import { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import G17Client from '../../../services/G17Client';
import { ReferralContainerForm } from '../../referrals/ReferralContainerForm';
import { useAppSelector } from '../../../reducers';
import { getRootOrg } from '../../../selectors/initiative';
import { toastFromError } from '@g17eco/molecules/toasts';
import { handleRouteError } from '../../../logger';
import { ProductCodes } from '../../../types/products';
import { Loader } from '@g17eco/atoms/loader';
import type { CreateSetupFn } from '@g17eco/types/app-integration';

interface Props {
  initiativeId: string;
  requiresProduct: ProductCodes;
  providerName: string;
  onCreate: CreateSetupFn;
}

type View = 'default' | 'referral' | 'activating';

export function PurchaseRequiredView({ initiativeId, requiresProduct, providerName, onCreate }: Props) {
  const [view, setView] = useState<View>('default');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const location = useLocation();
  const org = useAppSelector(getRootOrg);

  const searchParams = new URLSearchParams(location.search);
  const isCheckoutSuccess = searchParams.get('checkout') === 'success';

  useEffect(() => {
    if (!isCheckoutSuccess) {
      return;
    }
    setView('activating');
    onCreate({ generatedAnswers: [] })
      .catch((error) => {
        toastFromError({ error, title: 'Failed to activate integration' });
        setView('default');
      });
  }, [isCheckoutSuccess]);

  if (view === 'activating') {
    return (
      <div className='text-center p-4'>
        <Loader />
        <p className='mt-3'>Activating integration...</p>
      </div>
    );
  }

  const handlePurchase = () => {
    setIsRedirecting(true);
    G17Client.createCheckoutSession({
      initiativeId,
      productCodes: [requiresProduct],
      returnUrl: location.pathname,
    })
      .then((session) => {
        if (!session.url) {
          throw new Error('Checkout session URL is missing');
        }
        window.location.href = session.url;
      })
      .catch((error) => {
        setIsRedirecting(false);
        toastFromError({ error, title: 'Failed to start checkout' });
        handleRouteError(error, { initiativeId, requiresProduct });
      });
  };

  if (view === 'referral') {
    if (!org) {
      return (
        <div className='text-center p-4'>
          <Loader />
        </div>
      );
    }
    return (
      <div className='text-left d-lg-flex justify-content-center'>
        <div style={{ maxWidth: '360px' }}>
          <ReferralContainerForm
            backBtn={
              <Button color='link-secondary' onClick={() => setView('default')}>
                Back
              </Button>
            }
            onSubmit={() => setView('default')}
            org={org}
            productCode={requiresProduct}
          />
        </div>
      </div>
    );
  }

  if (isRedirecting) {
    return (
      <div className='text-center p-4'>
        <Loader />
        <p className='mt-3'>Redirecting to checkout...</p>
      </div>
    );
  }

  return (
    <div className='text-center p-4'>
      <h5>{providerName} requires a subscription to activate</h5>
      <p className='text-ThemeTextLight mt-2 mb-4'>
        Purchase the {providerName} addon or enter a referral code to get started.
      </p>
      <div className='d-flex justify-content-center gap-3'>
        <Button color='link-secondary' onClick={() => setView('referral')}>
          I have a referral code
        </Button>
        <Button color='primary' size='lg' onClick={handlePurchase}>
          Purchase
        </Button>
      </div>
    </div>
  );
}
